import styled from 'styled-components';
import { ContentsContainerRoot } from '../../../assets/styles/ContentsContainerRoot';
import { useTranslation } from 'react-i18next';
import { RefObject, useEffect, useRef, useState } from 'react';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import RangePicker, { onChangeRangeInput } from '../../../components/RangePicker';
import SelectBox from '../../../components/SelectBox';
import SearchSelectBoxSm from '../../../components/SearchSelectBoxSm';
import ShortcutButton from '../../../components/button/ShortcutButton';
import { BtnGhost } from '../../../components/Button';
import { useSetAuth } from '../../../utils/useSetAuth';
import { useRecoilValue } from 'recoil';
import { userState } from '../../../atoms';
import { logPost } from '../../../services/log';
import useSetListCount from '../../../utils/useSetListCount';
import { IComCdList, ISelectBox } from 'customTypes';
import { LIST_COUNT, COMPLETE_TYPE_LIST } from '../../../_constants';
import MultiSelect from '../../../components/MultiSelect';
import TuiGridWrapper from '../../../components/Table/TuiGridWrapper';
import TuiGrid from '../../../components/Table/TuiGrid';
import useTuiGridSize from '../../../hooks/useTuiGridSize';
import { useGetFrozenCount } from '../../../utils/getFrozenCount';
import { todayYYYYMMDD } from '../../../utils/formatDate';
import useOnKeydownF9 from '../../../utils/useOnKeydownF9';
import Input from '../../../components/Input';
import i18n from '../../../translation/i18n';
import { trimObject } from '../../../utils/trimObject';
import { apiGet } from '../../../services/_common';
import Portal from '../../../components/Portal';
import { useQuery } from '@tanstack/react-query';
import { setComCdListState } from '../../../utils/setComCdListState';
import CustomCircleChkRenderer from '../../../components/CustomCircleChkRenderer';
import CustomAddressRenderer from '../../../components/CustomAddressRenderer';
import MapContainer from '../../../components/Modal/MapViewModal';
import CustomBoxStyleRenderer from '../../../components/CustomBoxStyleRenderer';
import GetFetchStatus from '../../../hooks/GetFetchStatus';
import TravelViewModal from '../../../components/Modal/TravelViewModal';
import dayjs from 'dayjs';
import { createRoot } from 'react-dom/client';
import { useOutletContext } from 'react-router-dom';
import SearchFilterModal from '../../../components/Modal/SearchFilterModal';
import { showExcelButton } from '../../../utils/deviceAuth';

const Root = styled(ContentsContainerRoot)`
  /* react-multi-select css custom */
  .select__control {
    background-color: var(--tonal-light);
    border-color: var(--outline);

    &:hover {
      border-color: var(--outline);
    }

    .select__placeholder {
      color: var(--text-primary);
      font-size: 0.875rem;
    }

    .select__multi-value {
      background-color: var(--tonal-deep);
      color: var(--text-primary);

      .select__multi-value__label {
        color: var(--text-primary);
        font-size: 0.875rem;
      }
    }
  }

  .address-title {
    color: var(--text-tertiary);
    width: -webkit-fill-available;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .custom-box {
    display: flex;
    flex-direction: column;

    .custom-flex {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.1rem;
      font-size: 0.875rem;
      line-height: 1.4;
      color: var(--text-tertiary);

      .material-symbols-rounded {
        font-size: 0.8rem;
        line-height: 1.4;
        font-variation-settings: 'FILL' 0, 'wght' 600, 'GRAD' 0, 'opsz' 24;
      }
    }
  }
`;

class CustomHTMLRenderer {
  element;

  constructor(props: any) {
    this.element = document.createElement('div');
    const root = createRoot(this.element);
    const { val1, val2 } = props.columnInfo.renderer.options.data[props.rowKey];

    root.render(
      <div className='custom-box'>
        <div className='custom-flex'>{val1 ?? '-'}</div>
        <div className='custom-flex'>{val2 ?? '-'}</div>
      </div>
    );
  }

  getElement() {
    return this.element;
  }
}

const TravelList = () => {
  const userInfo = useRecoilValue(userState);
  const { eExcelYn, eListYn } = userInfo;
  const { auth } = useSetAuth();
  const { t } = useTranslation();
  const toDay = todayYYYYMMDD();
  const size = useOutletContext<any>();
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅

  const INIT_SCHEDULE_TYPE = { type: 'scheduleType', scheduleType: 'A', cdName: t('전체') };
  const INIT_SHARE_TYPE = { type: 'csCd', csCd: 'A', cdName: t('전체'), backColor: '', fontColor: '' };

  const [columns, setColumns] = useState<any[]>([]);
  const [tableState, setTableState] = useState([]);
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const { listCount, setListCount, patchUserMenuAPI } = useSetListCount(); // 검색줄수설정 훅
  const [searchOption, setSearchOption] = useState({ smName: '', contents: '', start: toDay, end: toDay });
  const [rangeState, setRangeState] = useState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);

  const [loading, setLoading] = useState<boolean>(false);
  const [excelBtn, setExcelBtn] = useState<boolean>(false);
  const [visibleRangePicker, setVisibleRangePicker] = useState<boolean>(false);
  const smNameRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const frozenCount = useGetFrozenCount();
  const [rowKey, setRowKey] = useState('');
  const [users, setUsers] = useState<{ label: string; value: string }[]>([]);
  const [openModal, setOpenModal] = useState<any>({ status: false, type: '', code: '', index: 0 });
  const [address, setAddress] = useState({ address1: '', latitude: '', longitude: '' });

  const [headList, setHeadList] = useState<any>([]); // 사내 - 본사 리스트
  const [siteList, setSiteList] = useState<any>([]); // 사내 - 현장 리스트
  const [ccCdList, setCcCdList] = useState<any>([]); // 차량 리스트
  const [hCd, setHcd] = useState({ type: 'hCd', hCd: 'A', cdName: t('전체') }); // 사내 - 본사
  const [sCd, setScd] = useState({ type: 'sCd', sCd: 'A', cdName: t('전체') }); // 사내 - 현장
  const [ccCd, setCcCd] = useState({ type: 'ccCd', ccCd: 'A', cdName: t('전체') }); // 차량
  const [scheduleType, setScheduleType] = useState(INIT_SCHEDULE_TYPE);
  const [csCd, setCsCd] = useState(INIT_SHARE_TYPE);
  const [completeCd, setCompleteCd] = useState({ type: 'completeCd', completeCd: 'A', cdName: t('전체') }); // 완료 여부
  const showExcelBtn = showExcelButton(auth.excelAuth);

  const { data: scheduleSets, isLoading: isLoadingScheduleSet } = GetFetchStatus({
    path: '/schedule/set',
    req: { cCd: userInfo.hCd },
    useLoginUserInfo: false,
    queryKey: 'tavel',
  });
  const [scheduleSetList, setScheduleSetList] = useState<ISelectBox[]>(scheduleSets || []);

  const { data: headInfoList } = useQuery(['/company/chead_tavel', userInfo.hCd], () => apiGet({ path: '/company/chead', req: { cCd: userInfo.hCd } }), { enabled: !!userInfo.hCd && !!userInfo.sCd }); // 본사정보
  const { data: siteInfoList } = useQuery(['/company/csite_tavel', userInfo.hCd], () => apiGet({ path: '/company/csite', req: { cCd: userInfo.hCd } }), { enabled: !!userInfo.hCd && !!userInfo.sCd }); // 사내정보
  const { data: carList } = useQuery(['/schedule/car_tavel', userInfo.hCd], () => apiGet({ path: '/schedule/car', req: { cCd: userInfo.hCd } }), { enabled: !!userInfo.hCd && !!userInfo.sCd }); // 사내정보

  const { data: companyUser } = useQuery(['/user/company_tavel', userInfo.hCd], () => getCompanyUserAPI(), { enabled: !!userInfo.hCd && !!userInfo.sCd }); // 사내 사용자

  useEffect(() => {
    setLoading(true);
    getTravelAPI().then((res: any) => {
      if (res.status === 200) {
        setComCdListState(LIST_COUNT, setListCountComCdList, false);
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '출장관리-출장현황',
          action: '조회',
          etc: ``,
        });
        setLoading(false);
      }
    });
  }, []);

  const getTravelAPI = async () => {
    const sObj = {
      cCd: userInfo.hCd,
      date1: searchOption.start.replaceAll('-', ''),
      date2: searchOption.end.replaceAll('-', ''),
    };
    const req = trimObject(sObj);
    const res = await apiGet({ path: '/schedule/travel', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      setLoading(false);

      const searchFilter = { hCd: hCd.hCd, sCd: sCd.sCd, csCd: csCd.csCd, smName: searchOption.smName, contents: searchOption.contents, ccCd: ccCd.ccCd, completeYn: completeCd.completeCd };
      const newData = data.map((v: any) => {
        return {
          ...v,
          startDate: `${v.sDate} ${v.sTime}`,
          finishDate: `${v.fDate} ${v.fTime}`,
          hsCd: v.hName && v.sName ? `${v.hName ?? '-'} / ${v.sName ?? '-'}` : '-',
          driver: v.dName ? `${v.dName ?? '-'} / ${v.users.map((user: any) => user.userName) ?? '-'}` : '-',
          carInfo: v.ccName && v.ccNum ? `${v.ccName ?? '-'} / ${v.ccNum ?? '-'}` : '-',
          csData: {
            cd: v.csCd,
            name: v.csName,
            backColor: v.backColor,
            fontColor: v.fontColor,
          },
          mapData: {
            address1: v.address,
            latitude: v.latitude,
            longitude: v.longitude,
          },
        };
      });

      const filteredData = newData.filter((item: any) => {
        return Object.entries(searchFilter).every(([key, filterValue]) => {
          if (filterValue === 'A' || filterValue === '') return true; // 필터 값이 'A'이면 기준 무시 (return true)
          if (key === 'smName' || key === 'contents') return item[key]?.toLowerCase()?.includes(filterValue?.toLowerCase());
          return item[key] === filterValue; // 그렇지 않으면 항목의 속성이 필터의 속성과 일치하는지 확인
        });
      });

      const filter2 = users.length > 0 ? filteredData.filter((el: any) => users.some((el2: any) => el.users.some((user: any) => user.userName === el2.value))) : filteredData;
      console.log('filter2', filter2);
      setTableState(filter2);
    } else {
      setLoading(false);
    }
    return res;
  };

  // 사내 - 본사 정보 조회
  useEffect(() => {
    if (headInfoList) {
      const { data } = headInfoList.data;
      const newArray = data.filter((el: any) => el.useYn === 'Y').map((el2: any) => ({ type: 'hCd', hCd: el2.hCd, cdName: el2.hName }));
      setHeadList([{ type: 'hCd', hCd: 'A', cdName: t('전체') }, ...newArray]);
    }

    // setTableState2(data.map((el: any) => ({ ...el, useYnCdName: el.useYn === 'Y' ? '사용' : '미사용' })));
    // setOrgTableState(data.map((el: any) => ({ ...el, useYnCdName: el.useYn === 'Y' ? '사용' : '미사용' })));
  }, [headInfoList]);

  // 사내 - 현장 정보 조회
  useEffect(() => {
    if (siteInfoList) {
      const { data } = siteInfoList.data;
      const newArray = data.filter((el: any) => el.useYn === 'Y' && el.hCd === hCd.hCd).map((el2: any) => ({ type: 'sCd', sCd: el2.sCd, cdName: el2.sName }));
      setSiteList([{ type: 'sCd', sCd: 'A', cdName: t('전체') }, ...newArray]);
    }
  }, [siteInfoList, hCd]);

  // 공유항목 조회
  useEffect(() => {
    if (scheduleSets) {
      const newArray: ISelectBox[] = scheduleSets
        .filter((cs: any) => cs.useYn === 'Y')
        .map((el: any) => ({ type: 'csCd', csCd: el.csCd, cdName: el.csName, backColor: el.backColor || '#3174ad', fontColor: el.fontColor || '#fff' }));
      setScheduleSetList([INIT_SHARE_TYPE, ...newArray]);
    }
  }, [scheduleSets]);

  // 차량 리스트 조회
  useEffect(() => {
    if (carList) {
      const { data } = carList.data;
      const newArray = data.filter((el: any) => el.useYn === 'Y').map((el2: any) => ({ type: 'ccCd', ccCd: el2.ccCd, cdName: `${el2.ccName}/${el2.ccNum}` }));
      setCcCdList([{ type: 'ccCd', ccCd: 'A', cdName: t('전체') }, ...newArray]);
    }
  }, [carList]);

  // 사내 사용자 조회
  const getCompanyUserAPI = async () => {
    const req = { hCd: userInfo.hCd };
    const res = await apiGet({ path: '/user/company', req });

    if (res.status === 200) {
      const YNFilter = res.data.data.filter((el: any) => el.approvalYn === 'Y' && el.useYn === 'Y'); // 승인 및 사용중인 사용자만 필터링
      const newMap = YNFilter.map((el: any) => ({ ...el, label: el.userName, value: el.userName }));
      return newMap;
    }

    return res;
  };

  useEffect(() => {
    setColumns([
      {
        header: t('시작일자/시간'),
        name: 'startDate',
        align: 'center',
        width: 130,
        renderer: {
          type: CustomHTMLRenderer,
          options: {
            data: tableState.map((el: any) => ({ val1: el.sDate, val2: el.sTime })),
          },
          classNames: ['text_secondary'],
        },
        sortable: true,
      },
      {
        header: t('종료일자/시간'),
        name: 'finishDate',
        align: 'center',
        width: 130,
        renderer: {
          type: CustomHTMLRenderer,
          options: {
            data: tableState.map((el: any) => ({ val1: el.fDate, val2: el.fTime })),
          },
          classNames: ['text_secondary'],
        },
        sortable: true,
      },
      {
        header: t('본사명/현장명'),
        name: 'hsCd',
        align: 'center',
        renderer: {
          type: CustomHTMLRenderer,
          options: {
            data: tableState.map((el: any) => ({ val1: el.hName, val2: el.sName })),
          },
        },
        minWidth: 120,
        sortable: true,
      },
      {
        header: t('주소'),
        name: 'mapData',
        align: 'center',
        renderer: {
          type: CustomAddressRenderer,
          options: {
            onClickMapView,
          },
        },
        minWidth: 160,
        sortable: true,
      },
      {
        header: t('공유항목'),
        name: 'csData',
        align: 'center',
        renderer: {
          type: CustomBoxStyleRenderer,
        },
        width: 100,
        sortable: true,
      },
      {
        header: t('제목'),
        name: 'smName',
        align: 'center',
        minWidth: 120,
        sortable: true,
      },
      {
        header: t('내용'),
        name: 'contents',
        align: 'center',
        minWidth: 120,
        sortable: true,
      },
      {
        header: t('운전자/참가자'),
        name: 'driver',
        align: 'center',
        renderer: {
          type: CustomHTMLRenderer,
          options: {
            data: tableState.map((el: any) => ({ val1: el.dName, val2: el.users.map((user: any) => user.userName).join(',') })),
          },
        },
        minWidth: 120,
        sortable: true,
      },
      {
        header: t('차량명/차량번호'),
        name: 'carInfo',
        align: 'center',
        renderer: {
          type: CustomHTMLRenderer,
          options: {
            data: tableState.map((el: any) => ({ val1: el.ccName, val2: el.ccNum })),
          },
        },
        minWidth: 120,
        sortable: true,
      },
      {
        header: t('완료'),
        name: 'completeYn',
        align: 'center',
        renderer: {
          type: CustomCircleChkRenderer,
        },
        width: 60,
        sortable: true,
      },
    ]);
  }, [i18n.language, tableState]);

  // 날짜 포멧 적용
  useEffect(() => {
    if (rangeState[0].startDate) {
      setSearchOption((prev: any) => ({ ...prev, start: dayjs(rangeState[0].startDate).format('YYYY-MM-DD') }));
    }
    if (rangeState[0].endDate) {
      setSearchOption((prev: any) => ({ ...prev, end: dayjs(rangeState[0].endDate).format('YYYY-MM-DD') }));
    }
  }, [rangeState[0].startDate, rangeState[0].endDate]);

  // f9 search
  useEffect(() => {
    if (isF9Pressed) {
      onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  // 지도보기 클릭
  const onClickMapView = (e: React.MouseEvent<HTMLElement>, mapData: any) => {
    e.stopPropagation();

    setOpenModal({ type: 'mapView', status: true });
    setAddress(mapData);
  };

  // 출장 Row 클릭
  const onClickRow = (rowData: any, columnName: any, filteredIndex: number) => {
    setRowKey(`${filteredIndex}`);
    // setAddress({ address: rowData.address, latitude: rowData.latitude, longitude: rowData.longitude });
    setOpenModal({ status: true, type: 'travelView', data: rowData, index: filteredIndex, title: '출장현황' });
  };

  // 검색 버튼 클릭
  const onClickSearch = (filterRef?: RefObject<HTMLDivElement>) => {
    setLoading(true);
    if (filterRef) {
      // 모바일일 경우 검색 필터 모달 창 닫기
      filterRef.current?.classList.remove('active');
      setOpenModal({ status: false, type: 'filter' });
    }
    getTravelAPI();
    getTravelAPI().then((res: any) => {
      if (res.status === 200) {
        setComCdListState(LIST_COUNT, setListCountComCdList, false);
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '출장관리-출장현황',
          action: '조회',
          etc: ``,
        });
        setLoading(false);
      }
    });
  };

  const onEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onClickSearch();
    }
  };

  // 초기화 버튼 클릭
  const onClickInitiateSearchOption = () => {
    Object.keys(searchOption).map((el: any) => {
      return setSearchOption((prev) => ({ ...prev, [el]: '', start: '', end: '' }));
    });
    setRangeState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
    setHcd({ type: 'hCd', hCd: 'A', cdName: t('전체') });
    setScd({ type: 'sCd', sCd: 'A', cdName: t('전체') });
    setCcCd({ type: 'ccCd', ccCd: 'A', cdName: t('전체') });
    setCsCd(INIT_SHARE_TYPE);
    setCompleteCd({ type: 'completeCd', completeCd: 'A', cdName: t('전체') });
    setUsers([]);
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave = () => {
    if (tableState.length <= 0) return;
    setExcelBtn(true);
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '근로자 연명부',
      action: '엑셀 저장',
      etc: ``,
    });
  };

  const renderExcelBtn = () => {
    if (showExcelBtn) {
      return (
        <BtnGhost onClick={onClickExcelSave}>
          <span className='icon-ms-xlsx'>X</span> {t('엑셀')}
        </BtnGhost>
      );
    }
    return null;
  };

  /* 엔터 검색 기능 [ 활성화시 F9키 처럼 횟수 제한 필요 ]
  const loginOnEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (smNameRef.current) onClickSearch();
    }
  };
  */

  const onClickRangeInput = () => {
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  // 필터 버튼 클릭
  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  return (
    <>
      <div className='content-container oneColumn'>
        <Root loading={loading}>
          <SearchOptions>
            {size.innerSize.W > 1024 ? (
              <>
                <div className='inputsWrapper'>
                  <div className='inputForm-row-fit'>
                    <span className='calendarLabel'>{t('기간')}</span>
                    <div className='inputForm-col'>
                      <div className='flex-basic'>
                        <input id='startInput' type='text' value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                        <span className='inputDash'> ~ </span>
                        <input id='endInput' type='text' value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                      </div>
                      {visibleRangePicker && (
                        <div className='rangePickerWrapper'>
                          <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} style={{ left: '82%' }} />
                        </div>
                      )}
                    </div>

                    <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                      <span className='material-symbols-rounded'>calendar_month</span>
                    </button>
                  </div>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='wWorkstatus'>{t('본사명')}</label>
                    <SelectBox options={headList} defaultOption={t('전체')} state={hCd} setState={setHcd} stateKey='hCd' initiateKey={hCd.hCd} filterbar='filter-1-left' />
                  </div>
                  <div className='inputForm-row withLabel'>
                    <label htmlFor='sjCd'>{t('현장명')}</label>
                    <div className='inputForm-col'>
                      <SelectBox options={siteList} defaultOption={t('전체')} state={sCd} setState={setScd} stateKey='sCd' initiateKey={sCd.sCd} filterbar='filter-1-left' optionHeight='height-md' />
                    </div>
                  </div>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='wForeignyn'>{t('공유항목')}</label>
                    <SelectBox
                      options={scheduleSetList}
                      defaultOption={t('전체')}
                      state={csCd}
                      setState={setCsCd}
                      stateKey='csCd'
                      initiateKey={csCd.csCd}
                      filterbar='filter-1-left'
                      optionHeight='height-md'
                      // clickAble={scheduleType.scheduleType !== '3'}
                      toastContent='공유일정을 선택하세요'
                      optionType='color'
                    />
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col'>
                      <Input placeholder={t('제목')} label='' type='text' id='smName' name='smName' state={searchOption} setState={setSearchOption} inputRef={smNameRef} />
                    </div>
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col'>
                      <Input
                        placeholder={t('내용')}
                        label=''
                        type='text'
                        id='contents'
                        name='contents'
                        state={searchOption}
                        setState={setSearchOption}
                        inputRef={contentRef}
                        // onKeyDown={loginOnEnterKeyDown}
                      />
                    </div>
                  </div>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='wWorkstatus'>{t('차량')}</label>
                    <SelectBox options={ccCdList} defaultOption={t('전체')} state={ccCd} setState={setCcCd} stateKey='ccCd' initiateKey={ccCd.ccCd} filterbar='filter-1-left' />
                  </div>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='sjCd'>{t('완료여부')}</label>
                    <SelectBox
                      options={COMPLETE_TYPE_LIST}
                      defaultOption={t('전체')}
                      state={completeCd}
                      setState={setCompleteCd}
                      stateKey='completeCd'
                      initiateKey={completeCd.completeCd}
                      filterbar='filter-1-left'
                    />
                  </div>
                </div>
                <div className='inputsWrapper'>
                  <div className='secondSearchOption'>
                    <div className='flex-basic textBtnGroup'>
                      <div className='inputForm-col withLabelComCf'>
                        <MultiSelect options={companyUser} value={users} setVal={setUsers} placeholder='참가자' />
                      </div>
                      <ShortcutButton icon='search' buttonText='검색' shortcut='F9' onClick={onClickSearch} />
                      <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                      <div className='searchResult'>
                        {t('총')}
                        <span>{tableState.length}</span>
                        {t('개')}
                      </div>
                      <div className='inputForm-row'>
                        <div className='inputForm-col withLabelComCf'>
                          <label htmlFor='useYn'>{t('보기 설정')}</label>
                          <SelectBox
                            options={listCountComCdList}
                            defaultOption={listCount.cdName}
                            state={listCount}
                            setState={setListCount}
                            stateKey={LIST_COUNT}
                            initiateKey={listCount[LIST_COUNT]}
                            setTableLinesAPI={patchUserMenuAPI}
                            optionHeight='height-sm'
                            rsearch
                          />
                        </div>
                      </div>
                    </div>
                    <div className='flex-basic iconBtnGroup'>{renderExcelBtn()}</div>
                  </div>
                </div>
              </>
            ) : (
              <div className='filter-wrapper'>
                <div className='inputsWrapper'>
                  <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                    <div className='inputForm-col'>
                      <Input
                        placeholder={t('제목')}
                        label=''
                        type='search'
                        id='smName'
                        name='smName'
                        state={searchOption}
                        setState={setSearchOption}
                        inputRef={smNameRef}
                        onKeyDown={onEnterKeyDown}
                        ico='search'
                      />
                    </div>
                  </div>
                  <BtnGhost onClick={onClickFilter} className='filter-btn'>
                    <span className='material-symbols-rounded'>filter_alt</span>
                  </BtnGhost>
                </div>
                <div className='inputsWrapper'>
                  <div className='flex-basic iconBtnGroup'>{renderExcelBtn()}</div>
                </div>
              </div>
            )}
          </SearchOptions>
          <TuiGridWrapper componentRef={componentRef}>
            <TuiGrid
              data={tableState}
              // filterTableState={filterTableState}
              // setFilterTableState={setFilterTableState}
              columns={columns}
              perPage={Number(listCount[LIST_COUNT])}
              excelBtn={excelBtn}
              setExcelBtn={setExcelBtn}
              usePagenation
              onClickRow={onClickRow}
              scrollX
              height={tuiHeight}
              rowKey={rowKey}
              fileName='출장 현황'
              eListYn={eListYn}
              eExcelYn={eExcelYn}
              rowHeight={48}
              frozenCount={frozenCount}
              setRowKey={setRowKey}
              idxKey='smIdx'
            />
          </TuiGridWrapper>
        </Root>
      </div>
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'mapView' && <MapContainer openModal={openModal} setOpenModal={setOpenModal} address={address} />}
        {openModal.status && openModal.type === 'travelView' && (
          <TravelViewModal openModal={openModal} setOpenModal={setOpenModal} apiList={tableState} setRowKey={setRowKey} listCount={Number(listCount[LIST_COUNT])} />
        )}
        {/* <TravelViewModal /> */}
      </Portal>
      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={onClickSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-col filter-underline filter-option-date'>
                <span className='calendarLabel'>{t('기간')}</span>
                <div className='inputForm-col'>
                  <div className='flex-basic' role='presentation' onClick={onClickRangeInput}>
                    <input id='startInput' type='text' readOnly value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                    <span className='inputDash'> ~ </span>
                    <input id='endInput' type='text' readOnly value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper'>
                      <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
              </div>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t('보기 설정')}</label>
                  <SelectBox
                    options={listCountComCdList}
                    defaultOption={listCount.cdName}
                    state={listCount}
                    setState={setListCount}
                    stateKey={LIST_COUNT}
                    initiateKey={listCount[LIST_COUNT]}
                    setTableLinesAPI={patchUserMenuAPI}
                    optionHeight='height-sm'
                    rsearch
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='wWorkstatus'>{t('본사명')}</label>
                  <SelectBox options={headList} defaultOption={t('전체')} state={hCd} setState={setHcd} stateKey='hCd' initiateKey={hCd.hCd} filterbar='filter-1-left' />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='sjCd'>{t('현장명')}</label>
                  <div className='inputForm-col'>
                    <SelectBox options={siteList} defaultOption={t('전체')} state={sCd} setState={setScd} stateKey='sCd' initiateKey={sCd.sCd} filterbar='filter-1-left' optionHeight='height-md' />
                  </div>
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='wForeignyn'>{t('공유항목')}</label>
                  <SelectBox
                    options={scheduleSetList}
                    defaultOption={t('전체')}
                    state={csCd}
                    setState={setCsCd}
                    stateKey='csCd'
                    initiateKey={csCd.csCd}
                    filterbar='filter-1-left'
                    optionHeight='height-md'
                    // clickAble={scheduleType.scheduleType !== '3'}
                    toastContent='공유일정을 선택하세요'
                    optionType='color'
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='wWorkstatus'>{t('차량')}</label>
                  <SelectBox options={ccCdList} defaultOption={t('전체')} state={ccCd} setState={setCcCd} stateKey='ccCd' initiateKey={ccCd.ccCd} filterbar='filter-1-left' />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='sjCd'>{t('완료여부')}</label>
                  <SelectBox
                    options={COMPLETE_TYPE_LIST}
                    defaultOption={t('전체')}
                    state={completeCd}
                    setState={setCompleteCd}
                    stateKey='completeCd'
                    initiateKey={completeCd.completeCd}
                    filterbar='filter-1-left'
                  />
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </>
  );
};

export default TravelList;
